<template>
    <div class="side-function-bar-highlighter">
        <div style="margin-bottom: 24px;">
            <!-- 通过绝对定位，将这部分平移到 CustomHighlight 组件上，以达成设计效果 -->
            <div class="side-function-bar-highlighter__section-title pa-relative pa-mt-2">
                {{ $t('snapBusiness.queryHighlight.customHighlight') }}
                <ElTooltip
                    :content="customHighlightTooltipContent"
                    :visibleArrow="false"
                    effect="light"
                    placement="top"
                    popperClass="side-function-bar-highlighter__tooltip"
                >
                    <PtIcon icon="SolidInfo" />
                </ElTooltip>
            </div>
            <CustomHighlight
                v-model="highlightPanelValue"
                :onChange="highlightPanelChangeHandler"
                :scrollHelper="scrollHelper"
                @mousedown.native.stop=";"
            />
        </div>
        <div>
            <div class="side-function-bar-highlighter__section-title">
                {{ $t('snapBusiness.queryHighlight.queryHighlight') }}
            </div>
            <QueryHighlight
                :currentQuery="currentQuery"
                :isLoading="backendKeywordsLoading"
                :backendKeywords="backendKeywords"
                @initHandler="switchBackendKeywordsHandler"
                @switchHandler="switchBackendKeywordsHandler"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import CustomHighlight, { messages as highlightPanelI18n, ScrollToHighlightHelper, fulFillHighlightListModel, isAllDisabled } from 'library-@/highlight/CustomHighlight';
import syncHighlightConfigToServer from 'library-@/highlight/mixins/sync';
import { getNodeArrayByValue, HIGHLIGHT_CHANNEL_IDENTITY } from '@patsnap-utils/highlight';
import QueryHighlight from 'library-@/components/QueryHighlight/QueryHighlight';
import { getViewParams } from 'library-@/utils/get-params';
import { NAMESPACE_PATENT_VIEW/* , ROUTES_DISABLE_HIGHLIGHT */ } from '../../../constants';
import { Tooltip } from 'element-ui';
import { Icon } from 'patsnap-biz';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { QueryHighlightService } from '@/domains/highlight/services/query-highlight-service';
import { CustomHighlightService } from '@/domains/highlight/services/custom-highlight-service';
import { usePatentBasic } from '@/composables/use-patent-basic';
import '@patsnap-ui/icon/assets/solid/info.svg';

const CONTAINER = '#overview-container';

class PatentViewScrollToHighlightHelper extends ScrollToHighlightHelper {
    getNodeArrayByValue(value) {
        return getNodeArrayByValue(value);
    }
}

export default {
    components: {
        CustomHighlight,
        QueryHighlight,
        ElTooltip: Tooltip,
        PtIcon: Icon,
    },
    mixins: [
        syncHighlightConfigToServer(NAMESPACE_PATENT_VIEW, HIGHLIGHT_CHANNEL_IDENTITY),
    ],
    props: {
        patentId: {
            type: String,
            required: true,
        },
    },
    setup() {
        const {
            getPatentBase,
        } = usePatentBasic();

        const queryHighlightService = injectService(QueryHighlightService);
        const customHighlightService = injectService(CustomHighlightService);

        const updateQueryHighlight = (value) => {
            queryHighlightService.updateQueryHighlight(value);
        };

        const setEnableKeywordHighlight = (value) => {
            customHighlightService.updateEnable(value);
        };

        return {
            updateQueryHighlight,
            setEnableKeywordHighlight,
            getPatentBase,
        };
    },
    data() {
        const model = fulFillHighlightListModel([]);

        return {
            lastIsAllDisabled: isAllDisabled(model),
            highlightPanelValue: model,
            scrollHelper: false,
            scrollContainer: CONTAINER,

            backendKeywordsLoading: false,
            backendKeywords: [],
            currentQuery: '',
        };
    },
    watch: {
        '$route.path'() {
            this.scrollContainer = '';
            this.$nextTick(() => {
                this.scrollContainer = CONTAINER;
            });
        },
        scrollContainer: {
            immediate: true,
            handler(eleSelector) {
                if (eleSelector) {
                    this.scrollHelper = new PatentViewScrollToHighlightHelper(eleSelector);
                } else {
                    if (this.scrollHelper) {
                        this.scrollHelper.destory();
                        this.scrollHelper = false;
                    }
                }
            },
        },
        syncHighlightConfigToServerMixin_initialKeywords(array) {
            const model = fulFillHighlightListModel(array);
            this.highlightPanelValue = model;
            // 通过 vuex 通知 densityBar 当前自定义高亮是否打开了
            const enabled = !isAllDisabled(model || []);
            this.setEnableKeywordHighlight(enabled);
        },
        patentId() {
            this.updateBackendKeywords();
        },
    },
    created() {
        this.customHighlightTooltipContent = _.get(highlightPanelI18n, `messages.${this.$i18n.locale}.PtHighlightPanel.splitNote`);
    },
    mounted() {
        this.updateBackendKeywords();
    },
    methods: {
        switchBackendKeywordsHandler(colors) {
            this.updateQueryHighlight(colors);
        },
        highlightPanelChangeHandler({ model, disabled = false/* , index, type, value */ }) {
            // const keywords = model.map(({ value }) => value);
            this.syncHighlightConfigToServerMixin_push(model, disabled);

            const curIsAllDisabled = isAllDisabled(model);
            if (this.lastIsAllDisabled !== curIsAllDisabled) {
                this.$emit('toggleEnable', !curIsAllDisabled);
                this.lastIsAllDisabled = curIsAllDisabled;
                this.setEnableKeywordHighlight(!curIsAllDisabled);
            }
        },
        async updateBackendKeywords() {
            try {
                const viewParams = getViewParams();
                if (!this.patentId) return;
                this.backendKeywordsLoading = true;
                const basicResp = await this.getPatentBase(this.patentId);
                const stemStatus = basicResp.stemming || false;

                if (viewParams && viewParams.folder_id) {
                    const { folder_id = '', qid = '' } = viewParams || {};
                    this.currentQuery = `_${folder_id}_${qid}&&stemStatus=${stemStatus}`;
                } else {
                    const params = _.pick(viewParams, ['semantic_id', 'job_id', 'sn', 'q']);
                    const q = params.q || '';
                    const id = params.semantic_id || params.job_id || params.sn || '';
                    const linkParamQ = `${id}_${q}`;
                    this.currentQuery = `${linkParamQ}&&stemStatus=${stemStatus}`;
                }

                let backendKeywords = _.get(basicResp, 'keywords', []);
                backendKeywords = _.map(backendKeywords, item => {
                    return _.isArray(item) ? item : [item];
                });
                this.backendKeywords = backendKeywords;
                this.backendKeywordsLoading = false;
            } catch (e) {
                this.backendKeywordsLoading = false;
                console.log('backendKeywords-error', e);
            }
        },
    },
};
</script>

<style lang="scss">
.side-function-bar-highlighter {
    position: relative;
    padding: 8px 16px;
    &__section-title {
        height: 24px;
        line-height: 24px;
        font-weight: 500;
        margin-bottom: 12px;
        color: rgb(6, 22, 50);
        .patsnap-biz-icon {
            cursor: pointer;
            fill: $text-disable !important;
        }
    }

    &__tooltip {
        line-height: 16px!important;
        max-width: 360px;
        box-shadow: 0 2px 8px 0 rgba(0,0,0,.2);
        border: none!important;
        background: #fff;
    }

    // 覆盖 @patsnap-ui/highlight-panel 组件内的样式，设计图与现有组件不一致
    .new-highlight__body {
        margin-bottom: 12px;
        .new-highlight__body__item{
            font-size: 0;
        }
    }
    .new-highlight__footer {
        td {
            vertical-align: top;
        }
    }

    // 覆盖 QueryHighlight 样式，以免影响其在 srp 下的样式
    .query-highlight-controller__checkbox-p {
        max-width: 185px;
    }
    .query-highlight-controller__keywords {
        max-width: 185px;
    }

    .query-highlight-controller__checkbox .patsnap-ui-checkbox .patsnap-ui-checkbox__input {
        vertical-align: inherit;
    }
}
.patent-view-app mark {
    background-color: transparent;
    color: inherit;
}
</style>
